import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SchoolProvider } from "@/context/SchoolContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import AiStudio from "@/pages/AiStudio";
import Schools from "@/pages/Schools";
import ParentSibling from "@/pages/ParentSibling";
import ParentDetail from "@/pages/ParentDetail";
import Students from "@/pages/Students";
import StudentProfile from "@/pages/StudentProfile";
import DeletedStudents from "@/pages/DeletedStudents";
import PromoteStudents from "@/pages/PromoteStudents";
import StudentEditor from "@/pages/StudentEditor";
import BulkEdit from "@/pages/BulkEdit";
import Subjects from "@/pages/exams/Subjects";
import Exams from "@/pages/exams/Exams";
import MarksEntry from "@/pages/exams/MarksEntry";
import ReportCards from "@/pages/exams/ReportCards";
import AdmitCards from "@/pages/exams/AdmitCards";
import Classes from "@/pages/Classes";
import { StreamsPage } from "@/pages/ClassMeta";
import Curriculum from "@/pages/academics/Curriculum";
import Syllabus from "@/pages/academics/Syllabus";
import Timetable from "@/pages/academics/Timetable";import FeeStructure from "@/pages/FeeStructure";
import FeeSettings from "@/pages/FeeSettings";
import FeesCard from "@/pages/FeesCard";
import CollectFees from "@/pages/CollectFees";
import EditFees from "@/pages/EditFees";
import DueFees from "@/pages/DueFees";
import ViewFees from "@/pages/ViewFees";
import FollowUp from "@/pages/FollowUp";
import Transport from "@/pages/Transport";
import Attendance from "@/pages/Attendance";
import Homework from "@/pages/Homework";
import IDCards from "@/pages/IDCards";
import AboutUs from "@/pages/AboutUs";
import Certificates from "@/pages/Certificates";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import AdminOverview from "@/pages/AdminOverview";
import AdminFees from "@/pages/AdminFees";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import CanvasDesigner from "@/pages/CanvasDesigner";
import VerifyPage from "@/pages/VerifyPage";
import Settings from "@/pages/Settings";
import AuditLogs from "@/pages/AuditLogs";
import Staff from "@/pages/Staff";
import StaffProfile from "@/pages/StaffProfile";
import BulkEditStaff from "@/pages/BulkEditStaff";
import DeletedStaff from "@/pages/DeletedStaff";
import Payroll from "@/pages/Payroll";
import Portal from "@/pages/Portal";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (["student", "parent"].includes(user.role)) return <Portal />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <AuthProvider>
      <SchoolProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/leads" element={<Protected><Leads /></Protected>} />
          <Route path="/ai" element={<Protected><AiStudio /></Protected>} />
          <Route path="/schools" element={<Protected><Schools /></Protected>} />
          <Route path="/parents" element={<Protected><ParentSibling /></Protected>} />
          <Route path="/parents/:key" element={<Protected><ParentDetail /></Protected>} />
          <Route path="/students" element={<Protected><Students /></Protected>} />
          <Route path="/students/deleted" element={<Protected><DeletedStudents /></Protected>} />
          <Route path="/students/bulk-edit" element={<Protected><BulkEdit /></Protected>} />
          <Route path="/students/promote" element={<Protected><PromoteStudents /></Protected>} />
          <Route path="/students/new" element={<Protected><StudentEditor /></Protected>} />
          <Route path="/students/:id/edit" element={<Protected><StudentEditor /></Protected>} />
          <Route path="/exams" element={<Protected><Exams /></Protected>} />
          <Route path="/exams/subjects" element={<Protected><Subjects /></Protected>} />
          <Route path="/exams/marks" element={<Protected><MarksEntry /></Protected>} />
          <Route path="/exams/reports" element={<Protected><ReportCards /></Protected>} />
          <Route path="/exams/admit-cards" element={<Protected><AdmitCards /></Protected>} />
          <Route path="/students/:id" element={<Protected><StudentProfile /></Protected>} />
          <Route path="/classes" element={<Protected><Classes /></Protected>} />
          <Route path="/classes/streams" element={<Protected><StreamsPage /></Protected>} />
          <Route path="/academics/curriculum" element={<Protected><Curriculum /></Protected>} />
          <Route path="/academics/syllabus" element={<Protected><Syllabus /></Protected>} />
          <Route path="/academics/timetable" element={<Protected><Timetable /></Protected>} />
          <Route path="/fees" element={<Protected><FeeStructure /></Protected>} />
          <Route path="/fees/collect" element={<Protected><CollectFees /></Protected>} />
          <Route path="/fees/collect/:studentId" element={<Protected><CollectFees /></Protected>} />
          <Route path="/fees/edit/:studentId" element={<Protected><EditFees /></Protected>} />
          <Route path="/fee-settings" element={<Protected><FeeSettings /></Protected>} />
          <Route path="/fees-card" element={<Protected><FeesCard /></Protected>} />
          <Route path="/view-fees" element={<Protected><ViewFees /></Protected>} />
          <Route path="/due-fees" element={<Protected><DueFees /></Protected>} />
          <Route path="/followup" element={<Protected><FollowUp /></Protected>} />
          <Route path="/transport" element={<Protected><Transport /></Protected>} />
          <Route path="/attendance" element={<Protected><Attendance /></Protected>} />
          <Route path="/homework" element={<Protected><Homework /></Protected>} />
          <Route path="/idcards" element={<Protected><IDCards /></Protected>} />
          <Route path="/about" element={<Protected><AboutUs /></Protected>} />
          <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/certificates/:type" element={<Protected><Certificates /></Protected>} />
          <Route path="/certificates" element={<Protected><Certificates /></Protected>} />
          <Route path="/staff" element={<Protected><Staff /></Protected>} />
          <Route path="/staff/bulk-edit" element={<Protected><BulkEditStaff /></Protected>} />
          <Route path="/staff/deleted" element={<Protected><DeletedStaff /></Protected>} />
          <Route path="/staff/:id" element={<Protected><StaffProfile /></Protected>} />
          <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
          <Route path="/users" element={<Protected><Users /></Protected>} />
          <Route path="/admin-overview" element={<Protected><AdminOverview /></Protected>} />
          <Route path="/templates" element={<Protected><Templates /></Protected>} />
          <Route path="/templates/new" element={<Protected><TemplateEditor /></Protected>} />
          <Route path="/templates/:id/edit" element={<Protected><TemplateEditor /></Protected>} />
          <Route path="/templates/designer" element={<Protected><CanvasDesigner /></Protected>} />
          <Route path="/templates/:id/designer" element={<Protected><CanvasDesigner /></Protected>} />
          <Route path="/verify/:id" element={<VerifyPage />} />
          <Route path="/admin-fees" element={<Protected><AdminFees /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/audit" element={<Protected><AuditLogs /></Protected>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </SchoolProvider>
    </AuthProvider>
  );
}

export default App;
